'use client';

import { APP_SCORING_FORMAT, APP_SCORING_MAX_SCORE } from '@/constants';

/**
 * Type representing the supported score formats.
 *
 *  - 'numerical': Displays the score as a raw number (e.g., "3.5").
 *  - 'numerical clamp': Clamps the score between 0 and the max score, then displays it (e.g., "4").
 *  - 'numerical grade': Converts the score to a grade format of "grade/maxScore" (e.g., "3/4").
 *  - 'numerical round': Rounds the score to the nearest integer and displays it (e.g., "4").
 *  - 'percentage': Converts the score to a percentage format (e.g., "87%").
 *
 * The default format is determined by the APP_SCORING_FORMAT constant, which
 * can be set to any of the above formats. If an unsupported format is provided,
 * it defaults to 'numerical clamp'.
 */
type TScoreFormat =
  | 'numerical'
  | 'numerical clamp'
  | 'numerical grade'
  | 'numerical round'
  | 'percentage';

/**
 * Converts a numerical score to a string representation based on the specified format.
 */
const convertToNumerical = (score: number) => {
  return score.toString();
};

/**
 * Converts a numerical score to a string representation, clamping it between 0 and the
 * specified maximum score.
 */
const convertToNumericalClamp = (score: number, maxScore: number) => {
  return Math.max(0, Math.min(maxScore, score)).toString();
};

/**
 * Converts a numerical score to a string representation in the format of "grade/maxScore",
 * where the grade is calculated by scaling the score to the maximum score and rounding it up.
 */
const convertToNumericalGrade = (score: number, maxScore: number) => {
  const gradeScore = Math.max(1, Math.min(maxScore, Math.ceil((score / maxScore) * maxScore)));
  return `${gradeScore}/${maxScore}`;
};

/**
 * Converts a numerical score to a string representation by rounding it to the nearest integer.
 */
const convertToNumericalRound = (score: number) => {
  return Math.round(score).toString();
};

/**
 * Converts a numerical score to a percentage string representation, formatted to two decimal places.
 */
const convertToPercentage = (score: number) => {
  return `${Math.round(score * 100)}%`;
};

/**
 * Converts a numerical score into the specified format.
 *
 * @param score - The numerical score to be converted.
 * @param maxScore - The maximum possible score. Defaults to APP_SCORING_MAX_SCORE.
 * @param format - The desired format for the score. Defaults to APP_SCORING_FORMAT.
 *
 * @returns The score formatted as a string according to the specified format.
 */
export function formatScore(
  score: number,
  maxScore: number = APP_SCORING_MAX_SCORE,
  format: TScoreFormat = APP_SCORING_FORMAT,
): string {
  switch (format) {
    case 'numerical':
      return convertToNumerical(score);
    case 'numerical clamp':
      return convertToNumericalClamp(score, maxScore);
    case 'numerical grade':
      return convertToNumericalGrade(score, maxScore);
    case 'numerical round':
      return convertToNumericalRound(score);
    case 'percentage':
      return convertToPercentage(score);
    default:
      return convertToNumericalClamp(score, maxScore);
  }
}
